<template>
    <section>
        <section v-if="state.isError">
            <div class="alert" v-text="$t('error.invalid_tx')" />
        </section>

        <div v-else class="tx-preview-desktop-container">
            <div class="card mobile-tx-preview">
                <mobile-overview-icon
                    v-bind:traceStatus="traceStatus"
                    v-bind:traceActions="simpleTxDetails"
                    v-bind:isPending="isPending"
                    v-bind:isTxSimple="isTxSimple"
                    v-bind:progress="progress"
                    v-bind:isTickTock="isTickTock"
                />
                <mobile-status
                    v-bind:traceStatus="traceStatus"
                    v-bind:isPending="isPending"
                    v-bind:traceActions="simpleTxDetails"
                />
                <mobile-overview-value
                    v-if="isTxSimple && traceStatus && !isTickTock"
                    v-bind:traceActions="simpleTxDetails"
                    v-bind:traceStatus="traceStatus"
                />
                <mobile-overview-address-list
                    v-bind:traceActions="simpleTxDetails"
                    v-bind:date="formattedDate"
                    v-bind:hash="hash"
                    v-bind:isTxSimple="isTxSimple"
                    v-bind:isTickTock="isTickTock"
                    v-bind:duration="duration"
                />
                <div class="mobile-tx-buttons-container">
                    <button class="mobile-tx-button" v-on:click="toggleShowDetails">
                        <icon-open-details />{{ $t('tx.details') }}
                    </button>
                    <button class="mobile-tx-button" v-on:click="shareTx">
                        <icon-share />{{ $t('tx.share') }}
                    </button>
                </div>
            </div>
        </div>

        <transaction-tabs
            style="margin-top: 18px"
            v-show="!state.isError && openDetails"
            v-bind:traceId="traceId"
            v-bind:timeStart="timeStart"
            v-bind:traceActions="traceActions"
            v-bind:traceEmpty="traceEmpty"
            v-bind:trace="trace"
            v-bind:isTickTock="isTickTock"
            v-bind:duration="duration"
            v-bind:warning="warning"
        />

        <div v-show="!state.isError && trace && !warning"
            v-bind:class="{'hide-trace': !openDetails }" class="trace">
            <Diagram
                v-bind:trace="trace"
                v-bind:connections="connections"
                v-on:node-clicked="handleNodeClicked"
            />
        </div>

        <template v-if="openDetails">
            <Transaction
                v-if="transactionData"
                v-bind:transactionData="transactionData"
                v-bind:style="warning ? 'margin-top: 18px' : ''"
            />

            <div class="open-details muted" v-else-if="Object.keys(trace).length > 0 && !state.isError">
                {{$t('tx.open_details')}}
                <!--<span class="open-details__link" @click="openFirstItem">{{$t('tx.click_on_item')}}</span>{{$t('tx.open_details')}}-->
            </div>

            <div class="transaction-button" v-if="state.showButton && !state.isLoading">
                <div class="transaction-button__items" v-on:click="handleButtonClicked">
                    <div class="transaction-button__icon">
                        <IconTxTypeOrdinary />
                    </div>
                    <div class="transaction-button__value">
                        <span>{{ $t('tx.button_transactions') }}</span>
                    </div>
                </div>
            </div>
        </template>
    </section>
</template>

<script>
import IconTxTypeOrdinary from '@primer/octicons/build/svg/git-commit-24.svg?inline';
import IconOpenDetails from '@img/icons/tonscan/details-mobile.svg?inline';
import IconShare from '@img/icons/tonscan/share-mobile.svg?inline';
import { goToDevExplorerMixin } from '~/mixins';
import { base64ToHex, toBase64Web } from '~/utils.js';
import { getTransactionTraceV3 } from '~/api/toncenterV3';
import { formatTimestamp } from '~/helpers';
import { getTransactionByHashOrInMessageHash, getTransactionByInMsgHash } from '~/api';
import { showToast } from '~/toast';
import { mapState } from 'vuex';
import Transaction from './Transaction/Transaction.vue';
import Diagram from './Diagram/Diagram.vue';
import TransactionTabs from './TransactionTabs.vue'; // import Status from './Tabs/Status.vue';
import MobileStatus from './Mobile/Status.vue';
import MobileOverviewIcon from './Mobile/OverviewIcon.vue';
import MobileOverviewValue from './Mobile/OverviewValue.vue';
import MobileOverviewAddressList from './Mobile/OverviewAddressList.vue'; // import EventOverview from './Tabs/EventOverview.vue';
// import EventOverview from './Tabs/EventOverview.vue';

export default {
    props: {
        hash: String,
    },

    data() {
        return {
            fees: {
                total: undefined,
                storage: undefined,
                other: undefined,
            },
            transactionInfo: {
                address: undefined,
                addressBook: undefined,
                type: undefined,
                isSuccess: undefined,
                exitCode: undefined,
                computeExitCode: undefined,
                actionResultCode: undefined,
                computeVmSteps: undefined,
                timestamp: undefined,
            },
            state: {
                isLoading: true,
                isError: false,
                msgOpen: false,
                redirect: false,
                showButton: false,
                msgHash: false,
                showPendingError: false,
            },
            hashData: {
                base64: undefined,
                hex: undefined,
                lt: undefined,
            },
            outMsgs: [],
            trace: {},
            traceId: null,
            timeStart: null,
            connections: [],
            transaction: null,
            transactionData: null,
            firstTransaction: null,
            traceStatus: undefined,
            traceTime: null,
            traceActions: undefined,
            traceEmpty: false,
            traceDetails: null,
            duration: undefined,
            progress: undefined,
            isPending: false,
            isTickTock: false,
            warning: false,
            txRawData: undefined,
        };
    },

    computed: {
        isGettingByMsgHash() {
            return this.$route.name === 'tx_by_msg_hash';
        },

        devExplorerUrl() {
            return `/transaction?account=${this.transactionInfo.address}&lt=${this.hashData.lt}&hash=${this.hashData.hex}`;
        },

        formattedDate() {
            if (!this.timeStart) return false;
            return formatTimestamp(this.timeStart);
        },

        isTxSimple() {
            if (!this.traceActions) return false;
            // Count how many actions are not associated with contracts
            const countRealActions = this.traceActions
                .filter(({ type }) => type !== 'call_contract' && type !== 'contract_deploy')
                .length;

            return countRealActions === 1;
        },

        simpleTxDetails() {
            if (!this.traceActions) return [];

            const realActions = this.traceActions
                .filter(({ type }) => type !== 'call_contract' && type !== 'contract_deploy');

            if (realActions.length > 0) return realActions;
            return this.traceActions;
        },

        openDetails() {
            if (!this.isTxSimple) return true;
            return this.openTxDetails;
        },
        ...mapState(['openTxDetails']),
    },

    created() {
        const hashLength = this.$route.params.hash.length;
        if (hashLength !== 64) {
            this.$router.replace(this.$localizeRoute({
                name: this.$route.name,
                params: { hash: base64ToHex(this.$route.params.hash) },
            }));
            this.state.redirect = true;
            return;
        }

        this.initData();
    },

    watch: {
        $route() {
            if (!this.state.redirect) {
                this.initData();
            }
        },
        transaction() {
            if (this.transaction) this.loadTransaction();
        },
        'state.redirect': 'loadTrace',
        'state.msgHash': 'loadTrace',
    },

    methods: {
        async initData() {
            // Waiting for all requests to load
            await Promise.all([
                this.loadData(),
                this.loadTrace(),
            ]);

            // Then we can show transaction data if there's warning (trace is too large)
            if (this.warning) {
                this.transactionData = this.txRawData;
            }
        },

        async loadData() {
            this.state.isLoading = true;
            this.state.isError = false;

            this.transactionData = null;

            try {
                const apiMethod = this.isGettingByMsgHash ? getTransactionByInMsgHash : getTransactionByHashOrInMessageHash;
                const { transactions, address_book } = await apiMethod(this.hash);
                const tx = transactions[0];

                this.txRawData = tx;

                this.updateTransactionInfo(tx, address_book);
                this.fixInMsgHash(tx);
            } catch (error) {
                // this.state.isError = true;
                console.error(error);
            } finally {
                this.state.isLoading = false;
            }
        },

        async loadTrace(reset = true) {
            if (reset) {
                this.state.isLoading = true;
                this.state.isError = false;
                this.transactionData = null;
                this.traceStatus = undefined;
                this.traceActions = undefined;
                this.trace = {};
                this.connections = [];
                this.progress = 0;
            }

            let newHash = base64ToHex(this.hash);
            // Get trace from toncenter
            try {
                if (this.$route.params.hash.length === 64) {
                    newHash = this.hash;
                }
                const res = await getTransactionTraceV3(newHash, !!this.state.msgHash);
                if (!res?.trace || !res?.events?.actions) {
                    showToast('Error loading the transaction. Trying again');
                    setTimeout(() => {
                        this.loadTrace(false);
                    }, 3000);
                    return;
                }
                this.isPending = res.pending;
                this.traceActions = res.events.actions;
                this.duration = res.duration;
                this.progress = res.progress;
                this.trace = res.trace;
                this.connections = res.connections;
                this.firstTransaction = res.trace.transaction?.hash;
                this.warning = res?.warning;

                if (res.events.actions.length === 0) {
                    this.traceEmpty = true;
                    if (this.firstTransaction) {
                        const checkTickTockTx = res?.events?.transactions[this.firstTransaction]?.description?.type;
                        if (checkTickTockTx && checkTickTockTx === 'tick_tock') {
                            this.isTickTock = true;
                            const tickTockTxDescription = res.events.transactions[this.firstTransaction].description;
                            this.traceStatus = tickTockTxDescription.compute_ph.success === true && !tickTockTxDescription?.action?.no_funds;
                        }
                    }
                }

                this.timeStart = res.series.timeStart * 1000;
                this.traceId = res.series.traceId;

                if (!this.isTickTock) {
                    const hasFailedStatus = this.traceActions.some(action => action.simple_preview.is_success === false);

                    this.traceStatus = !hasFailedStatus;
                }
            } catch (error) {
                this.state.isError = true;
                console.error(error);
            }

            if (this.isPending) {
                setTimeout(() => {
                    this.loadTrace(false);
                }, 5000);
            }
        },

        async loadTransaction() {
            try {
                /* eslint-disable-next-line no-inner-declarations */
                function findTransaction(trace, targetHash) {
                    if (trace.transaction.hash === targetHash) return trace.transaction;
                    // eslint-disable-next-line no-restricted-syntax
                    for (const child of trace.children || []) {
                        const result = findTransaction(child, targetHash);
                        if (result) return result;
                    }
                    return null;
                }

                this.transactionData = findTransaction(this.trace, this.transaction) || null;
            } catch (error) {
                console.error(error);
            }
        },

        updateTransactionInfo(tx, addressBook) {
            this.transactionInfo.addressBook = addressBook;
            this.transactionInfo.address = addressBook[tx.account].user_friendly;

            const newWalletTxSuccess = tx.description?.action?.result_code === undefined && tx.description?.compute_ph?.exit_code === undefined;
            const executionSuccess = tx.description?.action?.action_result_code !== null && parseInt(tx.description?.action?.result_code, 10) <= 1;
            this.transactionInfo.isSuccess = newWalletTxSuccess || executionSuccess;
            this.transactionInfo.exitCode = this.transactionInfo.isSuccess
                ? tx.description?.action?.result_code
                : tx.description?.compute_ph?.exit_code;
            this.transactionInfo.computeExitCode = tx.description?.compute_ph?.exit_code || null;
            this.transactionInfo.actionResultCode = tx.description?.action?.result_code || null;
            this.transactionInfo.computeVmSteps = tx.description?.compute_ph?.vm_steps;
            this.transactionInfo.timestamp = parseInt(`${tx.now}000`, 10);

            this.fees.total = tx.total_fees;
            this.fees.storage = tx.description?.storage_ph?.storage_fees_collected || 0;
            this.fees.other = tx.total_fees - this.fees.storage || 0;

            this.outMsgs = tx.out_msgs.map(Object.freeze);
            this.inMsg = tx.in_msg ? Object.freeze(tx.in_msg) : undefined;

            this.hashData.hex = base64ToHex(tx.hash);
            this.hashData.base64 = tx.hash;
            this.hashData.lt = tx.lt;
        },

        fixInMsgHash(tx) {
            if (tx?.in_msg?.hash && toBase64Web(tx.in_msg.hash) === toBase64Web(this.hash)) {
                this.state.msgHash = true;
                this.$router.replace(this.$localizeRoute({
                    name: 'tx_by_msg_hash',
                    params: { hash: toBase64Web(this.hash) },
                }));
            }
        },

        handleNodeClicked(data) {
            if (data?.transaction?.emulated) return;
            this.transaction = data.transaction.hash;
            this.state.showButton = false;
        },

        handleButtonClicked() {
            this.state.showButton = false;
            this.transaction = this.firstTransaction;
        },

        toggleShowDetails() {
            this.$store.commit('switchOpenTxDetails');
        },

        toggleMsg() {
            this.state.msgOpen = !this.state.msgOpen;
        },

        async shareTx() {
            const url = `https://tonscan.org/tx/${this.hash}`;
            const shareData = {
                title: `Transaction ${this.hash}`,
                url,
            };
            try {
                // Share on mobile
                await navigator.share(shareData);
            } catch (err) {
                // In case of error / desktop
                try {
                    await navigator.clipboard.writeText(url);
                } catch {
                    this.copyToClipboardFallback(url);
                }
                showToast('Link copied!');
            }
        },
    },

    metaInfo() {
        return {
            title: this.$t('tx.meta.title', { hash: this.hash }),
            meta: [{ property: 'robots', content: 'noindex' }],
        };
    },

    components: {
        Diagram,
        // Status,
        IconTxTypeOrdinary,
        Transaction,
        TransactionTabs,
        MobileStatus,
        MobileOverviewIcon,
        MobileOverviewValue,
        MobileOverviewAddressList,
        // EventOverview,
        IconOpenDetails,
        IconShare,
        // IconTrace,
    },

    mixins: [goToDevExplorerMixin],
};
</script>

<style lang="scss">
.tx-preview-desktop-container {
    //margin-top: 32px;
    width: 100%;
    display: flex;
    //height: calc(100dvh - 100px);
    align-items: center;
    //justify-content: center;
    flex-direction: column;
}
.hide-trace {
    pointer-events: none;
    opacity: 0;
}
.tx-status {
    display: flex;
    align-items: center;
    font-weight: 500;

    &--failed {
        color: var(--page-tx-status-error-color);
    }

    &--success {
        color: var(--page-tx-status-success-color);
    }

    &__icon {
        margin-right: 5px;
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
}

.tx-flow-schematics {
    display: flex;
    align-items: center;
    border: 1px solid var(--page-tx-flow-diagram-border-color);
    overflow: hidden;
    border-radius: 8px;
}

.tx-flow-schematics-step {
    display: flex;
    align-items: center;

    &__inner {
        padding: 4px 10px;
        display: flex;
        flex-direction: column;
    }

    &__phase {
        font-size: 12px;
    }

    &::after {
        content: '';
        display: block;
        width: 40px;
        height: 40px;
        border-color: var(--page-tx-flow-diagram-border-color);
        border-width: 0 1px 1px 0;
        border-style: solid;
        transform: rotate(-45deg);
        margin: 0 8px 0 -28px;
        user-select: none;
        pointer-events: none;
    }

    &:last-child::after {
        display: none;
    }
}

.trace {
    margin: 0;
}

.card-timeago {
    color: var(--body-muted-text-color);
}

.trace {
    margin: 20px 0;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.trace::-webkit-scrollbar {
    display: none;
}

.transaction-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 0;

    &__items {
        box-shadow: 0 0.5rem 1.2rem var(--card-box-shadow-color);
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;

        border: 1px solid var(--body-light-muted-color);
        padding: 0 12px;
        line-height: 36px;
        border-radius: 7px;
        color: var(--body-text-color);
        transition: all 0.2s;
    }

    &__items:hover {
        border: 1px solid var(--body-muted-text-color);
        color: var(--body-text-color);
        text-decoration: none;
    }

    &__icon {
        width: 24px;
        color: var(--body-text-color);

        svg {
            display: block;
            fill: currentColor;
        }
    }

    &__value {
        color: var(--body-text-color);
        font-size: 14px;
        text-transform: uppercase;
    }
}

.open-details {
    text-align: center;
    font-size: 14px;

    &__link {
        text-decoration: underline;
        text-decoration-style: dashed;
        text-decoration-thickness: 1px;
        text-underline-offset: 4px;
        cursor: pointer;

        &:hover {
            text-decoration: none;
        }
    }
}

.mobile-tx-preview {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 8px 12px 12px;
    align-items: center;
    box-sizing: border-box;

    &__time {
        font-size: 14px;
        margin: 6px 0;
    }
}

@media screen and (min-width: 600px) {
    .mobile-tx-preview {
        max-width: 480px;
    }
}

.mobile-tx-buttons-container {
    width: 100%;
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr 1fr;
    box-sizing: border-box;
    margin-top: 18px;
}

.mobile-tx-button {
    width: 100%;
    padding: 12px;
    border-radius: 8px;
    background-color: var(--badge-grey-background-color);
    color: var(--body-text-color);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
    text-transform: uppercase;
    display: flex;
    user-select: none;
    //flex-direction: column;
    justify-content: center;
    gap: 8px;

    &:hover {
        opacity: .88;
    }

    svg {
        width: 16px;
        height: 16px;
    }
}
</style>
